// ============================================================
// ArmoryScene — Hero Armory (account-level hero progression)
// ============================================================
//
// Layout:
//   Left column  — every hero available in Hero Select (account-wide list,
//                  not tied to a save slot). Selecting a hero shows its detail
//                  on the right.
//   Top-right    — selected hero's name, class, armory level + XP bar, and
//                  three equipped-passive slots (unlock at Lv 10 / 20 / 30).
//   Bottom-right — full strip of every passive this hero can equip. Click an
//                  unequipped passive to fill the next open slot; click an
//                  equipped one to unequip. Locked-by-level passives show the
//                  bronze Phaser-drawn padlock glyph — no emoji.
//
// Progression lives on `account.armoryByHero`, so every "Goomar" across every
// save slot shares XP + equipped passives.
// Run-start snapshot: when a run begins, each deployed hero's current passive
// list is copied into `hero.activePassiveIds` and frozen for the duration of
// the run so mid-run respecs can't retro-apply to this biome.

import Phaser from 'phaser';
import {
  ARMORY_RESPEC_COST_RUNES,
  ARMORY_PASSIVE_UNLOCK_LEVELS,
  CLASS_BASE_STATS,
  armoryFrameTier,
  getPassiveDef,
  getPassivesForHero,
} from '@roe/shared';
import type { Hero, HeroClass, PassiveDef } from '@roe/shared';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { HERO_OPTIONS } from './HeroSelectScene';
import { drawPadlock } from '../utils/glyphs';
import { drawHeroFrameOrnament, drawTitleFlourish } from '../utils/heroFrameOrnament';

type RosterEntry = { heroClass: HeroClass; heroKey?: string; name: string; classLabel: string; color: number };

export class ArmoryScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private roster: RosterEntry[] = [];
  private selectedIdx = 0;
  private heroListRows: Phaser.GameObjects.GameObject[] = [];
  private detailObjs: Phaser.GameObjects.GameObject[] = [];
  /** Passive strip scroll offset (0 = first row visible). Resets when the
   *  selected hero changes so scroll state doesn't leak between rosters. */
  private stripOffset = 0;
  /** Hero list scroll offset (0 = first hero visible). The roster grew past
   *  the viewport once Cyrith landed (13 entries × 44 px = 572 px > 504 px
   *  available), so the list now scrolls. Stored as a row index, not a
   *  pixel offset — keeps row alignment crisp at any scale. */
  private heroListOffset = 0;
  /** Bound wheel handler so we can detach on scene shutdown. */
  private wheelHandler?: (pointer: Phaser.Input.Pointer, _obj: unknown, _dx: number, dy: number) => void;

  constructor() { super({ key: 'ArmoryScene' }); }

  init(data: { gsm: GameStateManager }): void {
    this.gsm = data.gsm;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.cameras.main.setBackgroundColor('rgba(3, 2, 4, 0.98)');

    const W = GAME_W;
    const H = GAME_H;

    // Build the account-wide roster from HeroSelect's canonical option list.
    // Not tied to player.heroes — every hero the player can ever recruit shows
    // here, even if they're not on the current save slot.
    this.roster = HERO_OPTIONS
      .filter((h) => !h.hidden) // Zhorak etc. hidden until they're production-ready
      .map((h) => ({
        heroClass: h.heroClass,
        heroKey: h.heroKey,
        name: h.name,
        classLabel: h.classLabel,
        color: h.color,
      }));

    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0).setDepth(0).setInteractive();

    // Header
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    const armoryTitle = this.add.text(W / 2, 22, '⚒  Hero Armory', {
      fontSize: '16px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    // Bronze filigree on either side of the title — matches HeroSelectScene so the
    // two scenes feel like part of the same menu family.
    drawTitleFlourish(this, W / 2, 22, armoryTitle.width, 11);

    const closeBtn = this.add.text(W - 20, 22, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(11).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.closeScene());
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.closeScene());
    }

    const runes = this.gsm.player.resources.loot ?? 0;
    this.add.text(W - 60, 22, `◆ ${runes}`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(1, 0.5).setDepth(11);

    // ── "?" help icon — explains when Elite Perks take effect ─────────────
    const helpX = W - 110;
    const helpBg = this.add.circle(helpX, 22, 10, 0x0d0a08, 1)
      .setStrokeStyle(1, 0x8a6420).setDepth(11).setInteractive({ useHandCursor: true });
    const helpT = this.add.text(helpX, 22, '?', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(12);
    helpBg.on('pointerover', () => { helpBg.setStrokeStyle(1, 0xc8973a); helpT.setColor('#f0c040'); });
    helpBg.on('pointerout',  () => { helpBg.setStrokeStyle(1, 0x8a6420); helpT.setColor('#c8973a'); });
    helpBg.on('pointerdown', () => this.showPerkHelp());

    this.renderHeroList();
    this.renderDetail();
    this.installStripScroll();
  }

  // ── Elite Perks help overlay ──────────────────────────────────────────────
  private showPerkHelp(): void {
    const W = GAME_W, H = GAME_H;
    const elements: Phaser.GameObjects.GameObject[] = [];
    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.78)
      .setDepth(80).setInteractive();
    elements.push(overlay);

    const panelW = 460, panelH = 300;
    const panel = this.add.rectangle(W / 2, H / 2, panelW, panelH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0xc8973a).setDepth(81);
    elements.push(panel);

    const title = this.add.text(W / 2, H / 2 - panelH / 2 + 22, 'Elite Perks — How They Apply', {
      fontSize: '15px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(82);
    elements.push(title);

    const body =
      'Equipped perks are snapshotted at run start.\n' +
      'Mid-run respecs do not retroactively apply to the\n' +
      'active run — your current run keeps the perks it\n' +
      'started with.\n\n' +
      'The snapshot refreshes when you:\n' +
      '   • Start a new run from camp\n' +
      '   • Activate a Rune Stone (regular or Eternal)\n' +
      '   • Are defeated and begin a fresh run\n\n' +
      'Per-encounter stacks (Phantom Reflexes, Lethal\n' +
      'Tempo, etc.) reset between waves.';

    const bodyT = this.add.text(W / 2 - panelW / 2 + 24, H / 2 - panelH / 2 + 56, body, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      lineSpacing: 4,
    }).setDepth(82);
    elements.push(bodyT);

    const closeBtn = this.add.text(W / 2, H / 2 + panelH / 2 - 22, '[ Close ]', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(82).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#f0c040'));
    closeBtn.on('pointerout',  () => closeBtn.setColor('#c8973a'));
    elements.push(closeBtn);

    const close = (): void => { for (const el of elements) el.destroy(); };
    closeBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);
  }

  // ── Hero list (left column) ─────────────────────────────────────────────
  private renderHeroList(): void {
    for (const o of this.heroListRows) o.destroy();
    this.heroListRows = [];
    const startX = 24;
    const startY = 72;
    // Row sized to fit the entire hero roster (12 visible heroes) inside the
    // available viewport without forcing the player to scroll — Cyrith was
    // landing at index 10 and falling below the fold under the old 44 px
    // row height (10 visible rows max), so the user couldn't see her at all.
    // 12 × 40 = 480 px fits inside the 486 px band when bottom reserve is
    // trimmed from 30 → 18. Wheel scroll handler still works if more heroes
    // are added later — visibleRows recomputes from the current rowH.
    const rowH = 40;
    const VIEWPORT_H = GAME_H - startY - 18;
    const visibleRows = Math.max(1, Math.floor(VIEWPORT_H / rowH));
    const maxOffset = Math.max(0, this.roster.length - visibleRows);
    if (this.heroListOffset > maxOffset) this.heroListOffset = maxOffset;
    if (this.heroListOffset < 0) this.heroListOffset = 0;

    this.add.text(startX, startY - 18, 'Heroes', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'italic',
    }).setDepth(11);

    // Scroll-position indicator — shown only when the list overflows.
    if (maxOffset > 0) {
      const start = this.heroListOffset + 1;
      const end = Math.min(this.roster.length, this.heroListOffset + visibleRows);
      this.add.text(startX + 200, startY - 18, `${start}-${end} / ${this.roster.length}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
      }).setDepth(11);
    }

    this.roster.forEach((hero, i) => {
      // Clip rows outside the visible scroll window. Skipping the render
      // entirely (vs alpha=0) keeps the GameObject count lean and avoids
      // ghost click zones for rows the player can't see.
      if (i < this.heroListOffset || i >= this.heroListOffset + visibleRows) return;
      const y = startY + (i - this.heroListOffset) * rowH;
      const active = i === this.selectedIdx;
      const key = hero.heroKey ?? hero.heroClass;
      const info = this.gsm.getArmoryInfo(key);
      const bg = this.add.rectangle(startX, y, 280, rowH - 4, active ? 0x2a1810 : 0x0d0a08, 0.95)
        .setOrigin(0, 0).setStrokeStyle(1, active ? 0xc8973a : 0x5a3600)
        .setDepth(10).setInteractive({ useHandCursor: true });

      // Hero-sprite icon on the left edge — uses the same high-res character
      // spritesheet the game renders in battle (128×128 frame scaled to 32×32
      // uniform — integer-ish scale keeps the pixel art crisp). Replaces the
      // old bronze color swatch. Class-icon PNGs never existed on disk so
      // going through them would have fallen back to solid colors anyway.
      const spriteKey = `hero_${key}`;
      const iconX = startX + 26;
      const iconY = y + (rowH - 4) / 2;
      let iconObj: Phaser.GameObjects.GameObject;
      if (this.textures.exists(spriteKey)) {
        // 36×36 framed tile — sized down from 40 so the row can shrink to 40 px
        // and fit all 12 heroes inside the viewport (Cyrith was below the fold
        // at the old 44 px row height). Pixel-art still reads at this size
        // since the sheet is downscaled to a near-integer factor.
        const tileSize = 36;
        const iconBg = this.add.rectangle(iconX, iconY, tileSize, tileSize, 0x060404, 0.9)
          .setStrokeStyle(1, active ? 0xc8973a : 0x5a3600).setDepth(10);
        const sprite = this.add.sprite(iconX, iconY, spriteKey)
          .setDisplaySize(36, 36).setOrigin(0.5, 0.5).setDepth(11);
        sprite.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
        // Armory-tier frame ornament — same visual language as HeroSelectScene
        // and in-game portraits. Baseline tier 1 (gold pips) from Lv 1 so every
        // row has some filigree. Small tile → keep the ornament depth near the
        // icon layer (depth 12) so it sits on top of the tile but under the
        // active-row highlights.
        const ornTier = Math.max(1, armoryFrameTier(info.level));
        const orn = drawHeroFrameOrnament(this, iconX, iconY, tileSize, tileSize, ornTier, 12);
        for (const g of orn.graphics) this.heroListRows.push(g);
        this.heroListRows.push(iconBg);
        iconObj = sprite;
      } else {
        iconObj = this.add.rectangle(iconX, iconY, 8, 32, hero.color, 1).setDepth(11);
      }

      const nameT = this.add.text(startX + 54, y + 8, hero.name, {
        fontSize: '12px', fontFamily: 'Georgia, serif',
        color: active ? '#f0c040' : '#c8973a', fontStyle: 'bold',
      }).setDepth(11);
      const subT = this.add.text(startX + 54, y + 24, `Armory Lv ${info.level}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#8a6420',
      }).setDepth(11);
      const picksT = this.add.text(startX + 270, y + 14, `${info.passiveIds.length}/${info.unlockedSlots}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6aaa60',
      }).setOrigin(1, 0).setDepth(11);
      bg.on('pointerdown', () => {
        if (this.selectedIdx === i) return;
        this.selectedIdx = i;
        this.stripOffset = 0; // reset scroll when switching heroes
        this.renderHeroList();
        this.renderDetail();
      });
      this.heroListRows.push(bg, iconObj, nameT, subT, picksT);
    });
  }

  // ── Detail panel (right) ────────────────────────────────────────────────
  private renderDetail(): void {
    for (const o of this.detailObjs) o.destroy();
    this.detailObjs = [];
    const hero = this.roster[this.selectedIdx];
    if (!hero) return;
    const heroKey = hero.heroKey ?? hero.heroClass;
    const info = this.gsm.getArmoryInfo(heroKey);
    const panelX = 330;
    const panelY = 72;
    const panelW = GAME_W - panelX - 30;

    // Header: name only — class label removed per direction (player-visible
    // "class" terminology is being phased out; internal heroClass stays).
    const nameT = this.add.text(panelX, panelY, hero.name, {
      fontSize: '20px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#f0c040', fontStyle: 'bold',
    }).setDepth(11);
    this.detailObjs.push(nameT);

    // Base stats strip — same HP / Mana / ATK / DEF / SPD / CRIT / CRIT DMG
    // block that HeroSelect shows at the bottom of its scene. Keyed by
    // heroKey first, fall through to heroClass so amazon / dwarf / necromancer
    // / bear-form variants get their correct starting stats.
    const statsKey = (hero.heroKey ?? hero.heroClass) as keyof typeof CLASS_BASE_STATS;
    const stats = CLASS_BASE_STATS[statsKey] ?? CLASS_BASE_STATS[hero.heroClass as keyof typeof CLASS_BASE_STATS];
    if (stats) {
      const statLine = [
        `HP: ${stats.hp}`,
        `Mana: ${stats.maxMp}`,
        `ATK: ${stats.attack}`,
        `DEF: ${stats.defense}`,
        `SPD: ${stats.speed}`,
        `CRIT: ${(stats.critChance * 100).toFixed(0)}%`,
        `CRIT DMG: ${stats.critDamage}x`,
      ].join('   ·   ');
      const statsT = this.add.text(panelX, panelY + 30, statLine, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a',
      }).setDepth(11);
      this.detailObjs.push(statsT);
    }

    // Level + XP bar
    const xpY = panelY + 58;
    const levelT = this.add.text(panelX, xpY, `Armory Level ${info.level}${info.atMax ? '  (Max)' : ''}`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setDepth(11);
    this.detailObjs.push(levelT);

    const barX = panelX;
    const barY = xpY + 20;
    const barW = panelW - 40;
    const barH = 8;
    const barBg = this.add.rectangle(barX, barY, barW, barH, 0x0d0a08, 1)
      .setOrigin(0, 0).setStrokeStyle(1, 0x5a3600).setDepth(10);
    this.detailObjs.push(barBg);
    if (!info.atMax) {
      const pct = Math.min(1, info.xpIntoLevel / Math.max(1, info.xpToNext));
      const fill = this.add.rectangle(barX + 1, barY + 1, (barW - 2) * pct, barH - 2, 0x6aaa60, 0.85)
        .setOrigin(0, 0).setDepth(11);
      this.detailObjs.push(fill);
      const xpLabel = this.add.text(barX + barW / 2, barY + barH / 2, `${info.xpIntoLevel} / ${info.xpToNext} XP`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#e8d8a0',
      }).setOrigin(0.5).setDepth(12);
      this.detailObjs.push(xpLabel);
    } else {
      const capLabel = this.add.text(barX + barW / 2, barY + barH / 2, 'Armory Level cap reached', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8e8a0',
      }).setOrigin(0.5).setDepth(12);
      this.detailObjs.push(capLabel);
    }

    // 3 equipped-passive slots (compact). Shrunk from 180×110 → 140×80 to
    // free bottom-of-scene vertical space for the Hero Journal panel and to
    // match the perk-strip row size so the visual hierarchy reads cleaner.
    const slotsY = barY + 34;
    const slotW = 140;
    const slotH = 80;
    const slotGap = 14;
    const slotsTotal = 3 * slotW + 2 * slotGap;
    const slotsStartX = panelX + Math.max(0, Math.floor((panelW - slotsTotal) / 2));
    for (let i = 0; i < 3; i++) {
      const sx = slotsStartX + i * (slotW + slotGap);
      const unlocked = i < info.unlockedSlots;
      const passiveId = info.passiveIds[i];
      const def = getPassiveDef(passiveId);
      const slotBg = this.add.rectangle(sx, slotsY, slotW, slotH, unlocked ? 0x1a1008 : 0x0a0806, 0.95)
        .setOrigin(0, 0).setStrokeStyle(1, def ? 0xc8973a : unlocked ? 0x8a6420 : 0x3a2800)
        .setDepth(10);
      this.detailObjs.push(slotBg);

      const unlockLv = ARMORY_PASSIVE_UNLOCK_LEVELS[i];
      const slotHeader = this.add.text(sx + slotW / 2, slotsY + 6, unlocked ? `Slot ${i + 1}` : `Unlocks at Lv ${unlockLv}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: unlocked ? '#c8973a' : '#8a6420', fontStyle: 'italic',
      }).setOrigin(0.5, 0).setDepth(11);
      this.detailObjs.push(slotHeader);

      if (def) {
        // Equipped passive — icon + name, click row to unequip. Compact.
        const icon = this.add.text(sx + slotW / 2, slotsY + 30, def.icon, {
          fontSize: '18px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: '#e8c060', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(11);
        const nameP = this.add.text(sx + slotW / 2, slotsY + 50, def.name, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
          wordWrap: { width: slotW - 10 }, align: 'center',
        }).setOrigin(0.5).setDepth(11);
        const lockedHint = this.add.text(sx + slotW / 2, slotsY + 68, 'respec to change', {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#6b4020', fontStyle: 'italic',
        }).setOrigin(0.5).setDepth(11);
        this.detailObjs.push(icon, nameP, lockedHint);
      } else if (unlocked) {
        const emptyT = this.add.text(sx + slotW / 2, slotsY + 36, '(empty)', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
        }).setOrigin(0.5).setDepth(11);
        const hintT = this.add.text(sx + slotW / 2, slotsY + 54, 'Pick a perk below', {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#8a6420',
        }).setOrigin(0.5).setDepth(11);
        this.detailObjs.push(emptyT, hintT);
      } else {
        // Locked — Phaser-drawn padlock glyph, never an emoji.
        const lockG = this.add.graphics().setDepth(11);
        drawPadlock(lockG, sx + slotW / 2, slotsY + 44, 0x8a6420);
        this.detailObjs.push(lockG);
      }
    }

    // Respec button. ARMORY_RESPEC_COST_RUNES is kept in shared constants
    // (currently 0 — respec is free) so a future patch can reintroduce a cost
    // without touching this site's affordability / label logic.
    const respecY = slotsY + slotH + 16;
    const hasPicks = info.passiveIds.length > 0;
    const runes = this.gsm.player.resources.loot ?? 0;
    const canAfford = runes >= ARMORY_RESPEC_COST_RUNES;
    const respecEnabled = hasPicks && canAfford;
    const respecLabel = ARMORY_RESPEC_COST_RUNES > 0
      ? `Respec All Elite Perks  (◆ ${ARMORY_RESPEC_COST_RUNES})`
      : 'Respec All Elite Perks  (Free)';
    const respecBtn = this.add.text(panelX + panelW / 2, respecY,
      respecLabel, {
      fontSize: '11px', fontFamily: 'Georgia, serif',
      color: respecEnabled ? '#ef4444' : '#5a3600', fontStyle: 'bold',
      backgroundColor: '#0d0a08', padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(11);
    if (respecEnabled) {
      respecBtn.setInteractive({ useHandCursor: true });
      respecBtn.on('pointerover', () => respecBtn.setColor('#f87171'));
      respecBtn.on('pointerout', () => respecBtn.setColor('#ef4444'));
      respecBtn.on('pointerdown', () => {
        if (this.gsm.respecArmoryPassives(this.syntheticHero(hero))) {
          this.gsm.save();
          this.renderHeroList();
          this.renderDetail();
        }
      });
    }
    this.detailObjs.push(respecBtn);

    // ── Bottom strip: every passive this hero can equip ─────────────────
    this.renderPassiveStrip(hero, panelX, panelW, respecY + 26);

    // ── Hero Journal panel (bottom of the scene) ────────────────────────
    this.renderHeroJournal(hero, panelX, panelW);
  }

  /** Draws a compact 4-stat journal card at the bottom of the Armory detail
   *  panel. Reads account.armoryByHero[heroKey].journal (hydrated by
   *  ensureArmoryEntry) and lays out each stat in its own column for
   *  readability at a glance. Runs / Highest Stage / Kills / Bosses. */
  private renderHeroJournal(hero: RosterEntry, panelX: number, panelW: number): void {
    const JOURNAL_H = 74;
    const jy = GAME_H - JOURNAL_H - 6;
    const info = this.gsm.getArmoryState(hero.heroKey ?? hero.heroClass);
    const j = info.journal ?? { runsCompleted: 0, highestStage: 0, totalKills: 0, totalBossKills: 0 };

    const bg = this.add.rectangle(panelX, jy, panelW, JOURNAL_H, 0x0a0806, 0.95)
      .setOrigin(0, 0).setStrokeStyle(1, 0x5a3600).setDepth(10);
    this.detailObjs.push(bg);

    const title = this.add.text(panelX + 12, jy + 6, 'Hero Journal', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      fontStyle: 'italic',
    }).setDepth(11);
    this.detailObjs.push(title);

    const stats: Array<{ label: string; value: string }> = [
      { label: 'Runs',         value: `${j.runsCompleted}` },
      { label: 'Best Stage',   value: `${j.highestStage}` },
      { label: 'Total Kills',  value: `${j.totalKills}` },
      { label: 'Boss Kills',   value: `${j.totalBossKills}` },
    ];
    const colW = Math.floor((panelW - 20) / stats.length);
    const colsY = jy + 30;
    for (let i = 0; i < stats.length; i++) {
      const cx = panelX + 10 + colW * i + colW / 2;
      const s = stats[i];
      const valueTxt = this.add.text(cx, colsY, s.value, {
        fontSize: '18px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#f0c040', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(11);
      const labelTxt = this.add.text(cx, colsY + 20, s.label, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#8a6420',
      }).setOrigin(0.5).setDepth(11);
      this.detailObjs.push(valueTxt, labelTxt);
    }
  }

  /** Render the "all passives" strip below the equipped slots. Shows every
   *  passive for this hero — equipped, empty-slot-ready, and level-locked —
   *  so the player can browse the full pool at a glance. Scrolls when the
   *  roster (currently ≤ 5 per hero but pending quest unlocks will push it
   *  to ~10) overruns the viewport. */
  private renderPassiveStrip(hero: RosterEntry, panelX: number, panelW: number, y: number): void {
    const info = this.gsm.getArmoryInfo(hero.heroKey ?? hero.heroClass);
    const passives = getPassivesForHero(hero.heroClass, hero.heroKey);
    if (passives.length === 0) {
      const empty = this.add.text(panelX + panelW / 2, y + 40, `No Elite Perks authored for ${hero.name} yet.`, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(11);
      this.detailObjs.push(empty);
      return;
    }

    // Shrunk the row 56 → 44 and icon 22 → 18 so more perks fit without
    // scrolling (4-5 instead of 3-4). Bottom margin bumped 10 → 90 to
    // carve out the Hero Journal panel region.
    const rowH = 44;
    const rowGap = 5;
    const rowStride = rowH + rowGap;
    const rowW = panelW;
    const stripY = y + 22;
    const JOURNAL_H = 84;
    const visibleH = GAME_H - stripY - JOURNAL_H - 10;
    const visibleRows = Math.max(1, Math.floor(visibleH / rowStride));
    const maxOffset = Math.max(0, passives.length - visibleRows);
    if (this.stripOffset > maxOffset) this.stripOffset = maxOffset;

    const header = this.add.text(panelX, y,
      passives.length > visibleRows
        ? `Elite Perks  (${this.stripOffset + 1}-${Math.min(passives.length, this.stripOffset + visibleRows)} / ${passives.length})`
        : 'Elite Perks', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'italic',
    }).setDepth(11);
    this.detailObjs.push(header);

    // Scroll-cue chevrons (Phaser-drawn, no emoji) when there's content off-screen
    // above or below the visible window.
    if (this.stripOffset > 0) {
      const upG = this.add.graphics().setDepth(11);
      upG.fillStyle(0xc8973a, 0.9);
      upG.fillTriangle(panelX + panelW - 14, y + 2, panelX + panelW - 6, y + 2, panelX + panelW - 10, y - 4);
      this.detailObjs.push(upG);
    }
    if (this.stripOffset < maxOffset) {
      const dnG = this.add.graphics().setDepth(11);
      const dy = stripY + visibleRows * rowStride - 4;
      dnG.fillStyle(0xc8973a, 0.9);
      dnG.fillTriangle(panelX + panelW - 14, dy, panelX + panelW - 6, dy, panelX + panelW - 10, dy + 6);
      this.detailObjs.push(dnG);
    }

    const equippedSet = new Set(info.passiveIds);
    const slice = passives.slice(this.stripOffset, this.stripOffset + visibleRows);

    slice.forEach((def, localIdx) => {
      const ry = stripY + localIdx * rowStride;
      const isEquipped = equippedSet.has(def.id);
      const freeSlot = info.passiveIds.length < info.unlockedSlots;
      const rowBg = this.add.rectangle(panelX, ry, rowW, rowH, isEquipped ? 0x1a2a14 : 0x12100a, 0.95)
        .setOrigin(0, 0).setStrokeStyle(1, isEquipped ? 0x6aaa60 : 0x5a3600).setDepth(10);
      const icon = this.add.text(panelX + 22, ry + rowH / 2, def.icon, {
        fontSize: '18px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: isEquipped ? '#c8e8a0' : '#e8c060', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(11);
      const nameT = this.add.text(panelX + 44, ry + 6, def.name + (isEquipped ? '  (equipped)' : ''), {
        fontSize: '11px', fontFamily: 'Georgia, serif',
        color: isEquipped ? '#c8e8a0' : '#f0c040', fontStyle: 'bold',
      }).setDepth(11);
      const hasRightHint = !isEquipped && !freeSlot;
      const descRightPad = hasRightHint ? 130 : 18;
      const descT = this.add.text(panelX + 44, ry + 22, def.description, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#b8a078',
        wordWrap: { width: rowW - 44 - descRightPad },
      }).setDepth(11);
      this.detailObjs.push(rowBg, icon, nameT, descT);

      // Only unequipped perks with a free slot are clickable now. Equipped perks
      // are locked in — to change them the player must pay the respec cost.
      if (!isEquipped && freeSlot) {
        rowBg.setInteractive({ useHandCursor: true });
        rowBg.on('pointerover', () => rowBg.setStrokeStyle(1, 0xc8973a));
        rowBg.on('pointerout',  () => rowBg.setStrokeStyle(1, 0x5a3600));
        rowBg.on('pointerdown', () => {
          const synth = this.syntheticHero(hero);
          const openSlot = info.passiveIds.length as 0 | 1 | 2;
          if (this.gsm.equipArmoryPassive(synth, openSlot, def.id)) {
            this.gsm.save();
            this.renderHeroList();
            this.renderDetail();
          }
        });
      } else if (!isEquipped) {
        // Short, non-overlapping lock hint on the right.
        const lockG = this.add.graphics().setDepth(11);
        drawPadlock(lockG, panelX + rowW - 24, ry + rowH / 2, 0x6b4020);
        const msg = info.unlockedSlots === 0
          ? `Unlocks at Lv ${ARMORY_PASSIVE_UNLOCK_LEVELS[0]}`
          : 'Slots full';
        const hintT = this.add.text(panelX + rowW - 42, ry + rowH / 2, msg, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020', fontStyle: 'italic',
        }).setOrigin(1, 0.5).setDepth(11);
        this.detailObjs.push(lockG, hintT);
      }
    });
  }

  /** Attach a mouse-wheel handler — routes to the LEFT hero list when the
   *  cursor is over its column (x ≤ 320), otherwise scrolls the right
   *  passive strip. Detaches automatically on scene shutdown. */
  private installStripScroll(): void {
    this.wheelHandler = (pointer, _obj, _dx, dy) => {
      if (!this.scene.isActive()) return;
      const direction = dy > 0 ? 1 : -1;
      // Hero list owns the cursor while it's over the left column.
      const overHeroList = pointer.x <= 320;
      if (overHeroList) {
        const prev = this.heroListOffset;
        this.heroListOffset = Math.max(0, this.heroListOffset + direction);
        if (this.heroListOffset !== prev) this.renderHeroList();
        return;
      }
      const hero = this.roster[this.selectedIdx];
      if (!hero) return;
      const total = getPassivesForHero(hero.heroClass, hero.heroKey).length;
      if (total === 0) return;
      const prev = this.stripOffset;
      this.stripOffset = Math.max(0, this.stripOffset + direction);
      if (this.stripOffset !== prev) this.renderDetail();
    };
    this.input.on('wheel', this.wheelHandler);
    this.events.once('shutdown', () => {
      if (this.wheelHandler) this.input.off('wheel', this.wheelHandler);
    });
  }

  /** The GameStateManager armory methods expect a `Hero` (mostly for the
   *  heroKey/heroClass pair). The roster entry carries the same identity
   *  info, so we synthesise a minimal Hero-shaped object for the call. */
  private syntheticHero(entry: RosterEntry): Hero {
    return { heroClass: entry.heroClass, heroKey: entry.heroKey } as unknown as Hero;
  }

  private closeScene(): void {
    this.scene.stop();
    this.scene.start('MenuScene');
  }
}
